<script context="module">
	import { z } from "zod";

	// Placeholder GPT slot shapes until ad ops defines units. Invariant: every reserved min-height below
	// equals the tallest creative of its mapping row, and in_article stays inside the 640px article measure.
	export const GOOGLE_ADS_TYPES = {
		billboard: {
			collapse: false,
			sizes: ["fluid", [300, 50], [300, 100], [300, 250], [320, 50], [728, 90], [970, 50]],
			mapping: [
				[[980, 0], [[728, 90], [930, 100], [930, 130], [970, 90], [970, 250], [980, 120], [980, 150], [980, 200]]],
				[[414, 0], ["fluid", [320, 100], [320, 50], [300, 250], [300, 100], [300, 50]]],
				[[0, 0], ["fluid", [300, 250], [300, 100], [300, 50]]],
			],
		},
		billboard_small: {
			collapse: false,
			sizes: ["fluid", [300, 50], [300, 100], [320, 50], [728, 90], [970, 50]],
			mapping: [
				[[980, 0], [[728, 90], [930, 100], [930, 130], [970, 90], [980, 120], [980, 150], [980, 200]]],
				[[500, 0], ["fluid", [320, 100], [320, 50], [300, 100], [300, 50]]],
				[[0, 0], ["fluid", [320, 100], [320, 50], [300, 100], [300, 50]]],
			],
		},
		in_article: {
			sizes: ["fluid", [300, 250], [336, 280], [460, 60], [234, 60]],
			mapping: [
				[[728, 0], [[620, 100], [620, 200], [460, 60], [336, 280], [234, 60]]],
				[[414, 0], ["fluid", [320, 100], [300, 250], [336, 280], [234, 60], [300, 100]]],
				[[0, 0], ["fluid", [300, 250], [300, 100], [234, 60]]],
			],
		},
	};

	export function getAdDivId(id) {
		return `gid-ads-${String(id).trim().replaceAll("/", "-")}`;
	}

	// admin "Custom fields" tab of the AdPosition block (core Config reads DOMAIN_VIEWS…default.CustomFields)
	export const CustomFields = z.object({
		google_ad: z
			.object({
				adUnitPath: z.string(),
				type: z.enum(Object.keys(GOOGLE_ADS_TYPES)),
				minHeight: z.string().optional(),
			})
			.optional(),
	});

	// per-page/per-content rules are folded into context.ads_disabled by includes/ads.js
	export function showAds(context) {
		return context.ads_disabled !== true;
	}
</script>

<script>
	import { getContext } from "svelte";

	export let id;
	export let globalId = null;
	export let globalSource = null;
	export let data = {};
	export const type = "AdPosition";
	export let indexInContainer = undefined;
	globalSource || indexInContainer;

	const context = getContext("context");

	let show = showAds($context);
	if (
		($context.client?.is_mobile && data.for_mobile !== true) ||
		(!$context.client?.is_mobile && data.for_desktop !== true)
	) {
		show = false;
	}

	const googleAdConfig = data.custom_fields?.google_ad;
	const adType = googleAdConfig?.type && GOOGLE_ADS_TYPES[googleAdConfig.type] ? googleAdConfig.type : null;
	const divId = getAdDivId(id || globalId || "");

	let googleAd;
	if (show && googleAdConfig?.adUnitPath && adType) {
		const spec = GOOGLE_ADS_TYPES[adType];
		googleAd =
			`<script>window.googletag=window.googletag||{};googletag.cmd=googletag.cmd||[];googletag.cmd.push(function(){` +
			`googletag.defineSlot(${JSON.stringify(googleAdConfig.adUnitPath)},${JSON.stringify(spec.sizes)},${JSON.stringify(divId)})` +
			`.defineSizeMapping(${JSON.stringify(spec.mapping)})${spec.collapse === false ? ".setCollapseEmptyDiv(false)" : ""}` +
			`.addService(googletag.pubads());googletag.display(${JSON.stringify(divId)});});<\/script>`;
	}
</script>

{#if show}
	{#if googleAd}
		<div
			id={divId}
			data-ad-unit={googleAdConfig.adUnitPath}
			class="af-ad af-ad--{adType}"
			style={googleAdConfig.minHeight ? `min-height: ${googleAdConfig.minHeight}` : undefined}
		>
			{@html googleAd}
		</div>
	{:else}
		<slot />
	{/if}
{/if}

<style lang="scss">
	:global {
		.af-ad {
			position: relative;
			display: flex;
			justify-content: center;
			align-items: center;
			width: 100%;
			max-width: 100%;
			min-height: 250px;
			margin: 0 0 var(--af-space-4);
			overflow: hidden;
		}

		.af-ad::before {
			content: "Publicitate";
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			font: 500 11px / 1 var(--af-font-sans);
			letter-spacing: 0.08em;
			text-transform: uppercase;
			color: var(--af-ink-muted);
			opacity: 0.5;
			pointer-events: none;
		}

		.af-ad > * {
			position: relative;
		}

		.af-ad--billboard_small {
			min-height: 100px;

			@media (min-width: 980px) {
				min-height: 200px;
			}
		}

		.af-ad--in_article {
			@media (min-width: 414px) {
				min-height: 280px;
			}
		}
	}
</style>
